<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">My Shift Schedules</h5>
        <p class="page-sub">Your assigned shifts and work schedules</p>
      </div>
      <notification-bell />
    </div>

    <!-- ── Shift assignment notifications ───────────────────────── -->
    <div v-if="shiftNotifications.length" class="shift-notif-list">
      <div
        v-for="n in shiftNotifications"
        :key="n.id"
        class="shift-notif"
      >
        <div class="sn-icon"><font-awesome-icon icon="user-clock" /></div>
        <div class="sn-body">
          <p class="sn-title">New shift assigned</p>
          <p class="sn-msg">{{ n.data?.message }}</p>
        </div>
        <button class="sn-dismiss" @click="dismissNotification(n)" title="Dismiss">
          <font-awesome-icon icon="times" />
        </button>
      </div>
    </div>

    <div v-if="!employeeId" class="no-profile-box">
      <font-awesome-icon icon="user-slash" class="no-profile-icon" />
      <p class="no-profile-title">No employee profile linked</p>
      <p class="no-profile-sub">Contact your HR administrator to link your account to an employee record.</p>
    </div>

    <template v-else>

      <!-- Filter bar -->
      <div class="filter-bar">
        <!-- Status -->
        <select class="filter-select" v-model="filterStatus">
          <option value="all">All Shifts</option>
          <option value="active">Active</option>
          <option value="ended">Ended</option>
        </select>

        <!-- Period quick-select -->
        <div class="period-tabs">
          <button
            v-for="p in periodOpts"
            :key="p.value"
            class="period-tab"
            :class="{ 'period-tab--active': filterPeriod === p.value }"
            @click="setPeriod(p.value)"
          >{{ p.label }}</button>
        </div>

        <!-- Custom date range (only when period === 'custom') -->
        <template v-if="filterPeriod === 'custom'">
          <div class="date-range">
            <input type="date" class="filter-input" v-model="filterFrom" :max="filterTo || undefined" placeholder="From" />
            <span class="date-range-sep">–</span>
            <input type="date" class="filter-input" v-model="filterTo" :min="filterFrom || undefined" placeholder="To" />
          </div>
        </template>

        <!-- Client filter -->
        <search-select
          :key="filterClientKey"
          v-model="filterClient"
          url="/settings/customers/all"
          query-param="query"
          label-key="customer_name"
          value-key="id"
          response-key="data"
          placeholder="Filter by client…"
          search-placeholder="Type client name…"
          :min-chars="1"
          :extra-params="{ is_client: true }"
          class="filter-search-select"
        />

        <!-- Location filter -->
        <select class="filter-select" v-model="filterLocation">
          <option value="">All locations</option>
          <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
        </select>

        <!-- Shift type filter -->
        <select class="filter-select" v-model="filterShiftType">
          <option value="">All shift types</option>
          <option v-for="st in shiftTypes" :key="st.id" :value="st.id">{{ st.name }}</option>
        </select>

        <button class="btn-clear" v-if="hasActiveFilter" @click="resetFilters">
          <font-awesome-icon icon="times" /> Clear
        </button>

        <span class="filter-count">{{ meta.total }} shift{{ meta.total !== 1 ? 's' : '' }}</span>

        <!-- View toggle -->
        <div class="view-toggle">
          <button class="view-btn" :class="{ 'view-btn--active': viewMode === 'card' }" @click="viewMode = 'card'" title="Card view">
            <font-awesome-icon icon="th-large" />
          </button>
          <button class="view-btn" :class="{ 'view-btn--active': viewMode === 'table' }" @click="viewMode = 'table'" title="Table view">
            <font-awesome-icon icon="list" />
          </button>
        </div>
      </div>

      <!-- Skeleton -->
      <div v-if="loading" class="shifts-grid">
        <div v-for="n in 3" :key="n" class="sk-card">
          <div class="sk-head"></div>
          <div class="sk-body-lines">
            <div class="sk-line sk-line--md"></div>
            <div class="sk-line sk-line--sm"></div>
          </div>
        </div>
      </div>

      <!-- Empty: no shifts at all -->
      <div v-else-if="!assignments.length && !hasActiveFilter" class="empty-box">
        <font-awesome-icon icon="calendar-times" class="empty-icon" />
        <p class="empty-title">No shifts assigned yet</p>
        <p class="empty-sub">Your shift schedule will appear here once your HR manager assigns you to a shift.</p>
      </div>

      <!-- Empty: filters active -->
      <div v-else-if="!assignments.length && hasActiveFilter" class="empty-box">
        <font-awesome-icon icon="filter" class="empty-icon" />
        <p class="empty-title">No shifts match your filters</p>
        <button class="btn-outline" @click="resetFilters">Clear filters</button>
      </div>

      <!-- ══ Card view ══ -->
      <div v-else-if="viewMode === 'card'" class="shifts-grid">
        <div
          v-for="a in assignments"
          :key="a.id"
          class="shift-card"
          :class="{
            'shift-card--ended':  isEnded(a),
            'shift-card--today':  isToday(a),
          }"
          @click="openDetail(a)"
        >
          <!-- TODAY badge -->
          <div v-if="isToday(a)" class="today-badge">Today</div>

          <div class="sc-head">
            <div class="sc-icon" :class="isEnded(a) ? 'sc-icon--ended' : 'sc-icon--active'">
              <font-awesome-icon icon="calendar-alt" />
            </div>
            <div class="sc-info">
              <p class="sc-name">{{ a.shift?.name }}</p>
              <span class="sc-badge" :class="isEnded(a) ? 'badge--gray' : 'badge--green'">
                {{ isEnded(a) ? 'Ended' : 'Active' }}
              </span>
            </div>
            <font-awesome-icon icon="chevron-right" class="sc-arrow" />
          </div>

          <!-- Shift times -->
          <div class="sc-times">
            <div class="sc-time-block">
              <span class="sc-label">Start</span>
              <span class="sc-val">{{ fmtShiftTime(effectiveTimes(a).start) }}</span>
            </div>
            <span class="sc-sep">—</span>
            <div class="sc-time-block">
              <span class="sc-label">End</span>
              <span class="sc-val">{{ fmtShiftTime(effectiveTimes(a).end) }}</span>
            </div>
            <div class="sc-time-block" v-if="a.shift?.break_minutes && !a.rota_entry?.start_time">
              <span class="sc-label">Break</span>
              <span class="sc-val">{{ a.shift.break_minutes }}m</span>
            </div>
            <div class="sc-time-block" v-if="a.shift?.is_overnight">
              <span class="sc-badge badge--purple">Overnight</span>
            </div>
            <div class="sc-duration" v-if="shiftDuration(a)">
              <font-awesome-icon icon="hourglass-half" />
              {{ shiftDuration(a) }}
            </div>
          </div>

          <!-- Location -->
          <div v-if="a.shift?.location" class="sc-client">
            <font-awesome-icon icon="map-marker-alt" />
            {{ a.shift.location.name }}
          </div>

          <!-- Client -->
          <div v-if="a.shift?.client" class="sc-client">
            <font-awesome-icon icon="building" />
            {{ a.shift.client.customer_name }}
          </div>

          <!-- Shift date -->
          <div class="sc-shift-date" :class="a.rota_entry_id ? 'sc-shift-date--rota' : ''">
            <font-awesome-icon :icon="a.rota_entry_id ? 'calendar-check' : 'calendar-day'" />
            <span class="sc-date-text">{{ shiftDateLabel(a) }}</span>
            <span v-if="a.rota_entry_id" class="sc-rota-badge">Rota</span>
          </div>
          <!-- Rota name (rota-sourced only) -->
          <div v-if="a.rota_entry?.rota?.name" class="sc-rota-name">
            <font-awesome-icon icon="table" />
            {{ a.rota_entry.rota.name }}
          </div>

          <!-- Clock-in / clock-out actions (only on today's shift) -->
          <template v-if="isToday(a)">
            <template v-if="clockStatus?.clock_in && a.shift?.id && a.shift.id === (clockStatus.shift?.id ?? a.shift.id)">
              <div v-if="clockStatus.clock_out" class="clock-done">
                <font-awesome-icon icon="check-circle" />
                Clocked out at {{ fmtTime(clockStatus.clock_out) }}
              </div>
              <button v-else class="clock-btn clock-btn--out" :disabled="acting" @click.stop="handleClockOut(a)">
                <font-awesome-icon :icon="acting ? 'spinner' : 'sign-out-alt'" :spin="acting" />
                {{ acting ? 'Processing…' : 'Clock Out' }}
              </button>
            </template>
            <template v-else-if="!clockStatus?.clock_in">
              <button v-if="canShowClockIn(a)" class="clock-btn clock-btn--in" :disabled="acting" @click.stop="handleClockIn(a)">
                <font-awesome-icon :icon="acting ? 'spinner' : 'sign-in-alt'" :spin="acting" />
                {{ acting ? 'Processing…' : isEarlyIn(a) ? 'Clock In Early' : 'Clock In' }}
              </button>
              <div v-else class="clock-upcoming">
                <font-awesome-icon icon="clock" />
                Clock-in opens at {{ fmtShiftTime(effectiveTimes(a).start, -30) }}
              </div>
            </template>
          </template>
        </div>
      </div>

      <!-- ══ Table view ══ -->
      <div v-else class="tbl-wrap">
        <table class="shifts-tbl">
          <thead>
            <tr>
              <th>Shift</th>
              <th>Date</th>
              <th>Time</th>
              <th>Duration</th>
              <th>Location</th>
              <th>Client</th>
              <th>Rota</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="a in assignments"
              :key="a.id"
              class="tbl-row"
              :class="{ 'tbl-row--today': isToday(a), 'tbl-row--ended': isEnded(a) }"
              @click="openDetail(a)"
            >
              <td>
                <p class="tbl-shift-name">{{ a.shift?.name }}</p>
                <div class="tbl-badges">
                  <span v-if="isToday(a)"           class="sc-badge badge--today-sm">Today</span>
                  <span v-if="a.shift?.is_overnight" class="sc-badge badge--purple"  style="font-size:9px;">Overnight</span>
                </div>
              </td>
              <td class="tbl-date">{{ shiftDateLabel(a) }}</td>
              <td class="tbl-mono">{{ fmtShiftTime(effectiveTimes(a).start) }} – {{ fmtShiftTime(effectiveTimes(a).end) }}</td>
              <td class="tbl-mono tbl-dur">{{ shiftDuration(a) || '—' }}</td>
              <td class="tbl-client">
                <span v-if="a.shift?.location">
                  <font-awesome-icon icon="map-marker-alt" style="margin-right:4px;color:#7a90b0;font-size:10px;" />
                  {{ a.shift.location.name }}
                </span>
                <span v-else class="tbl-muted">—</span>
              </td>
              <td class="tbl-client">
                <span v-if="a.shift?.client">
                  <font-awesome-icon icon="building" style="margin-right:4px;color:#7a90b0;font-size:10px;" />
                  {{ a.shift.client.customer_name }}
                </span>
                <span v-else class="tbl-muted">—</span>
              </td>
              <td class="tbl-rota">
                <span v-if="a.rota_entry?.rota?.name" class="tbl-rota-name">
                  <font-awesome-icon icon="table" /> {{ a.rota_entry.rota.name }}
                </span>
                <span v-else class="tbl-muted">—</span>
              </td>
              <td>
                <span class="sc-badge" :class="isEnded(a) ? 'badge--gray' : 'badge--green'">
                  {{ isEnded(a) ? 'Ended' : 'Active' }}
                </span>
              </td>
              <td class="tbl-action" @click.stop>
                <template v-if="isToday(a)">
                  <template v-if="clockStatus?.clock_in && a.shift?.id && a.shift.id === (clockStatus.shift?.id ?? a.shift.id)">
                    <span v-if="clockStatus.clock_out" class="tbl-clocked">
                      <font-awesome-icon icon="check-circle" /> {{ fmtTime(clockStatus.clock_out) }}
                    </span>
                    <button v-else class="tbl-clock-btn tbl-clock-btn--out" :disabled="acting" @click="handleClockOut(a)">
                      <font-awesome-icon :icon="acting ? 'spinner' : 'sign-out-alt'" :spin="acting" />
                      Clock Out
                    </button>
                  </template>
                  <template v-else-if="!clockStatus?.clock_in">
                    <button v-if="canShowClockIn(a)" class="tbl-clock-btn tbl-clock-btn--in" :disabled="acting" @click="handleClockIn(a)">
                      <font-awesome-icon :icon="acting ? 'spinner' : 'sign-in-alt'" :spin="acting" />
                      {{ isEarlyIn(a) ? 'Clock In Early' : 'Clock In' }}
                    </button>
                    <span v-else class="tbl-muted" style="font-size:11px;">Opens {{ fmtShiftTime(effectiveTimes(a).start, -30) }}</span>
                  </template>
                </template>
                <button v-else class="tbl-detail-btn" @click="openDetail(a)" title="View details">
                  <font-awesome-icon icon="chevron-right" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ══ Pagination ══ -->
      <div v-if="meta.last_page > 1" class="pagination">
        <button class="pg-btn" :disabled="meta.current_page === 1" @click="goPage(meta.current_page - 1)">
          <font-awesome-icon icon="chevron-left" />
        </button>
        <button
          v-for="p in pageNumbers"
          :key="p"
          class="pg-btn"
          :class="{ 'pg-btn--active': p === meta.current_page, 'pg-btn--ellipsis': p === '…' }"
          :disabled="p === '…'"
          @click="p !== '…' && goPage(p)"
        >{{ p }}</button>
        <button class="pg-btn" :disabled="meta.current_page === meta.last_page" @click="goPage(meta.current_page + 1)">
          <font-awesome-icon icon="chevron-right" />
        </button>
        <span class="pg-info">{{ meta.from }}–{{ meta.to }} of {{ meta.total }}</span>
      </div>

    </template>

    <!-- ── Shift Detail Modal ── -->
    <div class="modal-overlay" v-if="detailModal.show" @click.self="detailModal.show = false">
      <div class="modal-box modal-box--detail">

        <!-- Header -->
        <div class="detail-head">
          <div class="detail-head-icon" :class="isEnded(detailModal.assignment) ? 'detail-head-icon--ended' : 'detail-head-icon--active'">
            <font-awesome-icon icon="calendar-alt" />
          </div>
          <div class="detail-head-info">
            <p class="detail-shift-name">{{ detailModal.assignment?.shift?.name }}</p>
            <div class="detail-badges">
              <span class="sc-badge" :class="isEnded(detailModal.assignment) ? 'badge--gray' : 'badge--green'">
                {{ isEnded(detailModal.assignment) ? 'Ended' : 'Active' }}
              </span>
              <span v-if="detailModal.assignment?.shift?.is_overnight" class="sc-badge badge--purple">Overnight</span>
              <span v-if="detailModal.assignment?.rota_entry_id" class="sc-badge badge--teal">Rota</span>
              <span v-if="isToday(detailModal.assignment)" class="sc-badge badge--today">Today</span>
            </div>
          </div>
          <button class="detail-close" @click="detailModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>

        <div class="detail-body">

          <!-- ── Date & Time ─────────────────────────────────── -->
          <div class="detail-section">
            <p class="detail-section-title"><font-awesome-icon icon="calendar-day" /> Schedule</p>
            <div class="detail-date">{{ shiftDateLabel(detailModal.assignment) }}</div>
            <div v-if="detailModal.assignment?.rota_entry?.rota?.name" class="detail-rota-name">
              <font-awesome-icon icon="table" />
              {{ detailModal.assignment.rota_entry.rota.name }}
            </div>
            <div class="detail-time-row">
              <div class="detail-time-block">
                <span class="detail-time-lbl">Start</span>
                <span class="detail-time-val">{{ fmtShiftTime(effectiveTimes(detailModal.assignment).start) }}</span>
              </div>
              <div class="detail-time-arrow"><font-awesome-icon icon="arrow-right" /></div>
              <div class="detail-time-block">
                <span class="detail-time-lbl">End</span>
                <span class="detail-time-val">{{ fmtShiftTime(effectiveTimes(detailModal.assignment).end) }}</span>
              </div>
              <div v-if="detailModal.assignment?.shift?.break_minutes && !detailModal.assignment?.rota_entry?.start_time" class="detail-time-block">
                <span class="detail-time-lbl">Break</span>
                <span class="detail-time-val">{{ detailModal.assignment.shift.break_minutes }}m</span>
              </div>
              <div v-if="shiftDuration(detailModal.assignment)" class="detail-duration">
                <font-awesome-icon icon="hourglass-half" /> {{ shiftDuration(detailModal.assignment) }}
              </div>
            </div>
          </div>

          <!-- ── Client & Location ───────────────────────────── -->
          <div v-if="detailModal.assignment?.shift?.client || detailModal.assignment?.shift?.location" class="detail-section">
            <p class="detail-section-title"><font-awesome-icon icon="building" /> Client &amp; Location</p>
            <div class="detail-client-card">
              <!-- Location -->
              <div v-if="detailModal.assignment.shift.location" class="detail-client-row detail-client-meta">
                <font-awesome-icon icon="map-marker-alt" />
                <span style="font-weight:600;color:#1a2b45;">{{ detailModal.assignment.shift.location.name }}</span>
              </div>
              <template v-if="detailModal.assignment.shift.client">
                <div class="detail-client-row">
                  <span class="detail-client-name">{{ detailModal.assignment.shift.client.customer_name }}</span>
                </div>
                <div v-if="detailModal.assignment.shift.client.email" class="detail-client-row detail-client-meta">
                  <font-awesome-icon icon="envelope" />
                  <span>{{ detailModal.assignment.shift.client.email }}</span>
                </div>
                <div v-if="detailModal.assignment.shift.client.phone_no || detailModal.assignment.shift.client.phone_number" class="detail-client-row detail-client-meta">
                  <font-awesome-icon icon="phone" />
                  <span>{{ detailModal.assignment.shift.client.phone_no || detailModal.assignment.shift.client.phone_number }}</span>
                </div>
                <div v-if="detailModal.assignment.shift.client.address" class="detail-client-row detail-client-meta">
                  <font-awesome-icon icon="map-marker-alt" />
                  <span>{{ detailModal.assignment.shift.client.address }}</span>
                </div>
                <!-- Map link when coordinates are available -->
                <a
                  v-if="detailModal.assignment.shift.client.latitude && detailModal.assignment.shift.client.longitude"
                  class="detail-map-link"
                  :href="`https://maps.google.com/?q=${detailModal.assignment.shift.client.latitude},${detailModal.assignment.shift.client.longitude}`"
                  target="_blank"
                  rel="noopener"
                  @click.stop
                >
                  <font-awesome-icon icon="map" /> Open in Maps
                </a>
              </template>
            </div>
          </div>

          <!-- ── Working With ────────────────────────────────── -->
          <div class="detail-section">
            <p class="detail-section-title"><font-awesome-icon icon="users" /> Working With</p>
            <div v-if="detailModal.loadingCoworkers" class="coworkers-loading">
              <div class="sk-line sk-line--md" style="height:40px;border-radius:10px;"></div>
              <div class="sk-line sk-line--sm" style="height:40px;border-radius:10px;margin-top:6px;"></div>
            </div>
            <div v-else-if="!detailModal.coworkers.length" class="coworkers-empty">
              <font-awesome-icon icon="user" />
              No other colleagues scheduled for this shift.
            </div>
            <div v-else class="coworkers-list">
              <div v-for="cw in detailModal.coworkers" :key="cw.id" class="coworker-row">
                <span class="coworker-avatar">{{ coworkerInitials(cw.employee) }}</span>
                <div class="coworker-info">
                  <span class="coworker-name">{{ coworkerName(cw.employee) }}</span>
                  <span v-if="cw.employee?.position?.name || cw.employee?.department?.name" class="coworker-meta">
                    {{ [cw.employee?.position?.name, cw.employee?.department?.name].filter(Boolean).join(' · ') }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Clock Action ────────────────────────────────── -->
          <div v-if="isToday(detailModal.assignment)" class="detail-section detail-section--clock">
            <template v-if="clockStatus?.clock_in && detailModal.assignment?.shift?.id && detailModal.assignment.shift.id === (clockStatus.shift?.id ?? detailModal.assignment.shift.id)">
              <div v-if="clockStatus.clock_out" class="clock-done">
                <font-awesome-icon icon="check-circle" /> Clocked out at {{ fmtTime(clockStatus.clock_out) }}
              </div>
              <button v-else class="clock-btn clock-btn--out" :disabled="acting" @click="handleClockOut(detailModal.assignment)">
                <font-awesome-icon :icon="acting ? 'spinner' : 'sign-out-alt'" :spin="acting" />
                {{ acting ? 'Processing…' : 'Clock Out' }}
              </button>
            </template>
            <template v-else-if="!clockStatus?.clock_in">
              <button v-if="canShowClockIn(detailModal.assignment)" class="clock-btn clock-btn--in" :disabled="acting" @click="handleClockIn(detailModal.assignment)">
                <font-awesome-icon :icon="acting ? 'spinner' : 'sign-in-alt'" :spin="acting" />
                {{ acting ? 'Processing…' : isEarlyIn(detailModal.assignment) ? 'Clock In Early' : 'Clock In' }}
              </button>
              <div v-else class="clock-upcoming">
                <font-awesome-icon icon="clock" />
                Clock-in opens at {{ fmtShiftTime(effectiveTimes(detailModal.assignment).start, -30) }}
              </div>
            </template>
          </div>

        </div>
      </div>
    </div>

    <!-- ── Early Clock-In Reason Modal ── -->
    <div class="modal-overlay" v-if="earlyModal.show" @click.self="earlyModal.show = false">
      <div class="modal-box">
        <div class="modal-header">
          <div class="modal-header-icon modal-header-icon--amber">
            <font-awesome-icon icon="clock" />
          </div>
          <div>
            <p class="modal-title">
              {{ earlyModal.type === 'in' ? 'Clocking In Early' : 'Clocking Out Early' }}
            </p>
            <p class="modal-sub">
              {{ earlyModal.type === 'in'
                ? `Shift starts at ${fmtShiftTime(effectiveTimes(earlyModal.shift).start)} — you're clocking in before that`
                : `Shift ends at ${fmtShiftTime(effectiveTimes(earlyModal.shift).end)} — you're clocking out before that`
              }}
            </p>
          </div>
        </div>
        <div class="modal-body">
          <label class="modal-label">
            Reason <span class="req">*</span>
          </label>
          <textarea
            class="modal-textarea"
            v-model="earlyModal.reason"
            :placeholder="earlyModal.type === 'in'
              ? 'e.g. Required to set up before shift, covering for a colleague…'
              : 'e.g. Appointment after work, supervisor approved early leave…'"
            rows="4"
            maxlength="500"
          ></textarea>
          <p class="modal-char">{{ earlyModal.reason.length }}/500</p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="earlyModal.show = false">Cancel</button>
          <button
            class="btn-confirm btn-confirm--amber"
            :disabled="earlyModal.reason.trim().length < 5 || acting"
            @click="confirmEarlyAction"
          >
            <font-awesome-icon :icon="acting ? 'spinner' : (earlyModal.type === 'in' ? 'sign-in-alt' : 'sign-out-alt')" :spin="acting" />
            {{ acting ? 'Processing…' : (earlyModal.type === 'in' ? 'Clock In Anyway' : 'Clock Out Anyway') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Location Bypass Reason Modal ── -->
    <div class="modal-overlay" v-if="locationModal.show" @click.self="locationModal.show = false">
      <div class="modal-box">
        <div class="modal-header">
          <div class="modal-header-icon" :class="locationModal.gpsUnavailable ? 'modal-header-icon--red' : 'modal-header-icon--orange'">
            <font-awesome-icon :icon="locationModal.gpsUnavailable ? 'location-slash' : 'map-marker-alt'" />
          </div>
          <div>
            <p class="modal-title">{{ locationModal.gpsUnavailable ? 'Location Unavailable' : 'Outside Designated Location' }}</p>
            <p class="modal-sub">
              {{ locationModal.gpsUnavailable
                ? 'GPS could not be detected. Provide a reason to clock in without location verification.'
                : 'You are outside the designated work location. Provide a reason to proceed.' }}
            </p>
          </div>
        </div>
        <div v-if="locationModal.locationInfo" class="location-dist-card">
          <span class="dist-name">
            <font-awesome-icon icon="building" style="margin-right:6px;color:#004AAD" />
            {{ locationModal.locationInfo.client_name }}
          </span>
          <span v-if="!locationModal.gpsUnavailable" class="dist-badge">
            <font-awesome-icon icon="route" />
            {{ locationModal.locationInfo.distance_meters }}m away · allowed radius {{ locationModal.locationInfo.geofence_radius }}m
          </span>
          <span v-else class="dist-badge dist-badge--red">
            <font-awesome-icon icon="exclamation-triangle" />
            GPS signal not available — cannot verify proximity
          </span>
        </div>
        <div class="modal-body">
          <label class="modal-label">Reason <span class="req">*</span></label>
          <textarea
            class="modal-textarea"
            v-model="locationModal.reason"
            :placeholder="locationModal.gpsUnavailable
              ? 'e.g. GPS disabled on device, indoor area with no signal…'
              : 'e.g. Working from client\'s head office, temporary reassignment…'"
            rows="4"
            maxlength="500"
          ></textarea>
          <p class="modal-char">{{ locationModal.reason.length }}/500</p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="locationModal.show = false">Cancel</button>
          <button
            class="btn-confirm btn-confirm--orange"
            :disabled="locationModal.reason.trim().length < 5 || acting"
            @click="clockInWithLocationReason"
          >
            <font-awesome-icon :icon="acting ? 'spinner' : 'sign-in-alt'" :spin="acting" />
            {{ acting ? 'Clocking in…' : 'Clock In Anyway' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useStore } from 'vuex';
import SearchSelect from '../../../../components/SearchSelect.vue';
import NotificationBell from '../../../../components/NotificationBell.vue';

export default {
  name: 'MyShiftsPage',
  components: { SearchSelect, NotificationBell },
  data() {
    return {
      store: useStore(),
      loading: false,
      acting: false,
      assignments: [],
      clockStatus: null,
      filterStatus: 'all',
      filterPeriod: 'all',
      filterFrom: '',
      filterTo: '',
      filterClient: '',
      filterClientKey: 0,
      filterShiftType: '',
      filterLocation: '',
      shiftTypes: [],
      locations: [],
      periodOpts: [
        { value: 'all',        label: 'All'        },
        { value: 'this_week',  label: 'This week'  },
        { value: 'next_week',  label: 'Next week'  },
        { value: 'this_month', label: 'This month' },
        { value: 'custom',     label: 'Custom'     },
      ],
      now: new Date(),
      nowTimer: null,
      pendingCoords: null,
      pendingShift: null,
      earlyModal:    { show: false, type: null, shift: null, reason: '' },
      locationModal: { show: false, reason: '', locationInfo: null, earlyReason: '', gpsUnavailable: false },
      detailModal:        { show: false, assignment: null, coworkers: [], loadingCoworkers: false },
      shiftNotifications: [],
      viewMode:    'card',
      pageSize:    12,
      currentPage: 1,
      meta: { total: 0, current_page: 1, last_page: 1, per_page: 10, from: 0, to: 0 },
    };
  },
  computed: {
    employeeId() {
      return this.store.state.getLoggedInUser?.employee_id || null;
    },
    todayStr() {
      const d = this.now;
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    // Resolved [from, to] ISO strings for the active period filter, or null if no range.
    periodRange() {
      const today = this.todayStr;
      const d     = new Date(today);

      if (this.filterPeriod === 'this_week') {
        const dow  = d.getDay(); // 0=Sun
        const mon  = new Date(d); mon.setDate(d.getDate() - ((dow + 6) % 7));
        const sun  = new Date(mon); sun.setDate(mon.getDate() + 6);
        return [mon.toISOString().slice(0, 10), sun.toISOString().slice(0, 10)];
      }
      if (this.filterPeriod === 'next_week') {
        const dow  = d.getDay();
        const mon  = new Date(d); mon.setDate(d.getDate() - ((dow + 6) % 7) + 7);
        const sun  = new Date(mon); sun.setDate(mon.getDate() + 6);
        return [mon.toISOString().slice(0, 10), sun.toISOString().slice(0, 10)];
      }
      if (this.filterPeriod === 'this_month') {
        const from = today.slice(0, 7) + '-01';
        const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        return [from, last.toISOString().slice(0, 10)];
      }
      if (this.filterPeriod === 'custom') {
        const f = this.filterFrom || null;
        const t = this.filterTo   || null;
        if (f || t) return [f, t];
      }
      return null;
    },

    hasActiveFilter() {
      return this.filterStatus !== 'all' || this.filterPeriod !== 'all' || !!this.filterClient || !!this.filterShiftType || !!this.filterLocation;
    },

    pageNumbers() {
      const total = this.meta.last_page;
      const cur   = this.meta.current_page;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = new Set([1, total, cur]);
      if (cur > 1) pages.add(cur - 1);
      if (cur < total) pages.add(cur + 1);
      const sorted = [...pages].sort((a, b) => a - b);
      const result = [];
      let prev = 0;
      for (const p of sorted) {
        if (p - prev > 1) result.push('…');
        result.push(p);
        prev = p;
      }
      return result;
    },
  },
  watch: {
    filterStatus()    { this.currentPage = 1; this.fetchShifts(); },
    filterShiftType() { this.currentPage = 1; this.fetchShifts(); },
    filterLocation()  { this.currentPage = 1; this.fetchShifts(); },
    filterClient()    { this.currentPage = 1; this.fetchShifts(); },
    periodRange:      { deep: true, handler() { this.currentPage = 1; this.fetchShifts(); } },
  },
  mounted() {
    this.fetchShiftTypes();
    this.fetchLocations();
    this.fetchShiftNotifications();
    if (this.employeeId) {
      this.fetchShifts();
      this.fetchClockStatus();
    }
    // Refresh `now` every 30 seconds so button visibility stays live
    this.nowTimer = setInterval(() => { this.now = new Date(); }, 30000);
  },
  beforeUnmount() {
    clearInterval(this.nowTimer);
  },
  methods: {
    // ── Shift notifications ───────────────────────────────────────────────────
    async fetchShiftNotifications() {
      try {
        const { data } = await axios.get('/hr/notifications', {
          params: { length: 20, unread_only: true },
        });
        const all = data.data?.notifications?.data || [];
        this.shiftNotifications = all.filter(n => n.data?.type === 'shift_assignment');
      } catch {/* silent */}
    },

    async dismissNotification(n) {
      this.shiftNotifications = this.shiftNotifications.filter(x => x.id !== n.id);
      try {
        await axios.put(`/hr/notifications/${n.id}/read`);
      } catch {/* silent */}
    },

    // ── Detail modal ─────────────────────────────────────────────────────────
    async openDetail(a) {
      this.detailModal = { show: true, assignment: a, coworkers: [], loadingCoworkers: true };
      try {
        const shiftId   = a.shift_id || a.shift?.id;
        const workDate  = a.date_from?.slice(0, 10);
        const myEmpId   = this.employeeId;
        const { data }  = await axios.get(`/hr/shift/${shiftId}`);
        const all       = data.data?.assignments || [];
        // Keep only other employees whose assignment covers the work date
        this.detailModal.coworkers = all.filter(cw => {
          if (cw.employee_id === myEmpId) return false;
          if (workDate) {
            const f = cw.date_from?.slice(0, 10);
            const t = cw.date_to?.slice(0, 10);
            if (f && f > workDate) return false;
            if (t && t < workDate) return false;
          }
          return true;
        });
      } catch { /* co-workers unavailable, show empty state */ }
      finally { this.detailModal.loadingCoworkers = false; }
    },

    coworkerName(emp) {
      if (!emp) return 'Unknown';
      return emp.full_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown';
    },
    coworkerInitials(emp) {
      if (!emp) return '?';
      return ((emp.first_name?.[0] || '') + (emp.last_name?.[0] || '')).toUpperCase() || '?';
    },

    // ── Data ─────────────────────────────────────────────────────────────────
    async fetchShifts() {
      this.loading = true;
      try {
        const params = { length: this.pageSize, page: this.currentPage };
        if (this.filterStatus !== 'all') params.status = this.filterStatus;
        if (this.filterLocation)  params.location_id   = this.filterLocation;
        if (this.filterClient)    params.client_id     = this.filterClient;
        if (this.filterShiftType) params.shift_type_id = this.filterShiftType;
        const range = this.periodRange;
        if (range) {
          if (range[0]) params.date_from = range[0];
          if (range[1]) params.date_to   = range[1];
        }
        const { data } = await axios.get('/hr/self-service/shifts', { params });
        this.assignments   = data.data?.data || [];
        const d            = data.data || {};
        this.meta = {
          total:        d.total        ?? 0,
          current_page: d.current_page ?? 1,
          last_page:    d.last_page    ?? 1,
          per_page:     d.per_page     ?? this.pageSize,
          from:         d.from         ?? 0,
          to:           d.to           ?? 0,
        };
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load shifts');
      } finally {
        this.loading = false;
      }
    },
    async fetchShiftTypes() {
      try {
        const { data } = await axios.get('/hr/shift-type/active');
        this.shiftTypes = data.data || [];
      } catch { /* non-critical */ }
    },
    async fetchLocations() {
      try {
        const { data } = await axios.get('/admin/location/active');
        this.locations = data.data || [];
      } catch { /* non-critical */ }
    },
    async fetchClockStatus() {
      try {
        const { data } = await axios.get('/hr/attendance/today-status', {
          params: { employee_id: this.employeeId },
        });
        this.clockStatus = data.data || null;
      } catch { /* silent */ }
    },

    // ── Date / time helpers ──────────────────────────────────────────────────
    isEnded(a) {
      if (!a.date_to) return false;
      return a.date_to.slice(0, 10) < this.todayStr;
    },
    isToday(a) {
      if (a.rota_entry_id) {
        // Rota entry is for a single specific date
        return a.date_from?.slice(0, 10) === this.todayStr;
      }
      const from = a.date_from?.slice(0, 10);
      const to   = a.date_to?.slice(0, 10);
      if (!from) return false;
      if (from > this.todayStr) return false;
      if (to && to < this.todayStr) return false;
      return true;
    },
    effectiveTimes(a) {
      return {
        start: a?.rota_entry?.start_time || a?.shift?.start_time || null,
        end:   a?.rota_entry?.end_time   || a?.shift?.end_time   || null,
      };
    },
    shiftStartMs(a) {
      const t = this.effectiveTimes(a).start;
      if (!t) return null;
      const [h, m] = t.split(':');
      const d = new Date(this.now);
      d.setHours(Number(h), Number(m), 0, 0);
      return d.getTime();
    },
    shiftEndMs(a) {
      const t = this.effectiveTimes(a).end;
      if (!t) return null;
      const [h, m] = t.split(':');
      const d = new Date(this.now);
      d.setHours(Number(h), Number(m), 0, 0);
      return d.getTime();
    },
    canShowClockIn(a) {
      if (!this.isToday(a)) return false;
      if (this.clockStatus?.clock_in) return false;
      const startMs = this.shiftStartMs(a);
      if (!startMs) return true; // no start time configured → always show
      return this.now.getTime() >= startMs - 30 * 60 * 1000;
    },
    isEarlyIn(a) {
      const startMs = this.shiftStartMs(a);
      if (!startMs) return false;
      return this.now.getTime() < startMs;
    },
    isEarlyOut(a) {
      const endMs = this.shiftEndMs(a);
      if (!endMs) return false;
      return this.now.getTime() < endMs;
    },

    // ── Clock-in flow ────────────────────────────────────────────────────────
    async handleClockIn(a) {
      this.pendingShift = a;
      if (this.isEarlyIn(a)) {
        this.earlyModal = { show: true, type: 'in', shift: a, reason: '' };
        return;
      }
      await this.doClockIn(null);
    },

    async confirmEarlyAction() {
      if (this.earlyModal.reason.trim().length < 5) return;
      const reason = this.earlyModal.reason.trim();
      this.earlyModal.show = false;
      if (this.earlyModal.type === 'in') {
        await this.doClockIn(reason);
      } else {
        await this.doClockOut(reason);
      }
    },

    async doClockIn(earlyReason) {
      this.acting = true;
      try {
        const coords = await this.getCoords();
        this.pendingCoords = coords;

        // GPS unavailable but this shift has a client with a set location → ask for reason upfront
        const client = this.pendingShift?.shift?.client;
        if (!coords && client?.latitude && client?.longitude) {
          this.locationModal = {
            show: true,
            reason: '',
            locationInfo: { client_name: client.customer_name, client_address: client.address || '' },
            earlyReason: earlyReason || '',
            gpsUnavailable: true,
          };
          return;
        }

        await axios.post('/hr/attendance/clock-in', {
          employee_id:           this.employeeId,
          shift_id:              this.pendingShift?.shift?.id || undefined,
          latitude:              coords?.lat ?? null,
          longitude:             coords?.lng ?? null,
          early_clockin_reason:  earlyReason || undefined,
        });
        this.$toast?.success('Clocked in!');
        this.fetchClockStatus();
      } catch (e) {
        if (e.response?.status === 428) {
          this.locationModal = {
            show: true,
            reason: '',
            locationInfo: e.response.data.data,
            earlyReason: earlyReason || '',
            gpsUnavailable: false,
          };
        } else {
          this.$toast?.error(e.response?.data?.message || 'Clock-in failed');
        }
      } finally {
        this.acting = false;
      }
    },

    async clockInWithLocationReason() {
      if (this.locationModal.reason.trim().length < 5) return;
      this.acting = true;
      try {
        await axios.post('/hr/attendance/clock-in', {
          employee_id:             this.employeeId,
          shift_id:                this.pendingShift?.shift?.id || undefined,
          latitude:                this.pendingCoords?.lat ?? null,
          longitude:               this.pendingCoords?.lng ?? null,
          bypass_location_check:   true,
          location_bypass_reason:  this.locationModal.reason.trim(),
          early_clockin_reason:    this.locationModal.earlyReason || undefined,
        });
        this.locationModal.show = false;
        this.$toast?.success('Clocked in with location note recorded');
        this.fetchClockStatus();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Clock-in failed');
      } finally {
        this.acting = false;
      }
    },

    // ── Clock-out flow ───────────────────────────────────────────────────────
    async handleClockOut(a) {
      if (this.isEarlyOut(a)) {
        this.earlyModal = { show: true, type: 'out', shift: a, reason: '' };
        return;
      }
      await this.doClockOut(null);
    },

    async doClockOut(earlyReason) {
      this.acting = true;
      try {
        const coords = await this.getCoords();
        await axios.post('/hr/attendance/clock-out', {
          employee_id:            this.employeeId,
          latitude:               coords?.lat ?? null,
          longitude:              coords?.lng ?? null,
          early_clockout_reason:  earlyReason || undefined,
        });
        this.$toast?.success('Clocked out!');
        this.fetchClockStatus();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Clock-out failed');
      } finally {
        this.acting = false;
      }
    },

    // ── Utilities ────────────────────────────────────────────────────────────
    getCoords() {
      return new Promise(resolve => {
        if (!navigator.geolocation) { resolve(null); return; }
        navigator.geolocation.getCurrentPosition(
          p  => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => resolve(null),
          { timeout: 5000 }
        );
      });
    },
    goPage(p) {
      this.currentPage = Math.max(1, Math.min(p, this.meta.last_page));
      this.fetchShifts();
    },
    setPeriod(value) {
      this.filterPeriod = value;
      if (value !== 'custom') {
        this.filterFrom = '';
        this.filterTo   = '';
        this.currentPage = 1;
        this.fetchShifts();
      }
    },
    resetFilters() {
      this.filterStatus    = 'all';
      this.filterPeriod    = 'all';
      this.filterFrom      = '';
      this.filterTo        = '';
      this.filterLocation  = '';
      this.filterClient    = '';
      this.filterShiftType = '';
      this.filterClientKey++;
      this.currentPage     = 1;
      this.fetchShifts();
    },
    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    fmtTime(dt) {
      if (!dt) return '—';
      return new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },
    shiftDuration(a) {
      const times = this.effectiveTimes(a);
      if (!times.start || !times.end) return null;
      const toMins = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
      let mins = toMins(times.end) - toMins(times.start);
      if (mins <= 0) mins += 1440;
      // Only deduct break when employee is on the full shift hours (no partial slot override)
      if (!a?.rota_entry?.start_time && !a?.rota_entry?.end_time) {
        mins -= (a?.shift?.break_minutes || 0);
      }
      if (mins <= 0) return null;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m ? `${h}h ${m}m` : `${h}h`;
    },
    // Returns the next date this assignment is "due", used for sort order.
    // Rota entries have a fixed work date. Regular assignments that are already
    // active treat today as their next occurrence so they sort relative to now,
    // not relative to when the assignment was created.
    nextOccurrenceDate(a) {
      if (a.rota_entry_id) {
        return a.date_from?.slice(0, 10) || this.todayStr;
      }
      const from = a.date_from?.slice(0, 10) || this.todayStr;
      // Assignment already started: treat today as the next occurrence
      return from <= this.todayStr ? this.todayStr : from;
    },

    // Returns the ISO date string that represents this assignment's actual work date.
    // For rota assignments, date_from === date_to === work_date.
    // For regular assignments, falls back to shift_date then date_from.
    assignmentWorkDate(a) {
      return (a.shift?.shift_date || a.date_from)?.slice(0, 10) ?? null;
    },

    shiftDateLabel(a) {
      if (a.rota_entry_id) {
        // Specific rota date — show full weekday + date
        const d = a.date_from?.slice(0, 10);
        if (!d) return '—';
        const dt = new Date(d);
        return dt.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
      }
      // Regular assignment — show date range
      const from = a.shift?.shift_date
        ? this.fmtDate(a.shift.shift_date)
        : a.date_from ? this.fmtDate(a.date_from) : null;
      const to   = a.date_to ? this.fmtDate(a.date_to) : null;
      if (!from) return '—';
      if (!to || from === to) return `From ${from}`;
      return `${from} – ${to}`;
    },

    fmtShiftTime(t, offsetMinutes = 0) {
      if (!t) return '—';
      const [h, m] = t.split(':').map(Number);
      const total  = h * 60 + m + offsetMinutes;
      const hh     = Math.floor(((total % 1440) + 1440) % 1440 / 60);
      const mm     = ((total % 1440) + 1440) % 1440 % 60;
      const ampm   = hh >= 12 ? 'PM' : 'AM';
      const h12    = hh % 12 || 12;
      return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
    },
  },
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.no-profile-box   { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:16px; }
.no-profile-icon  { font-size:48px; color:#dde5f0; }
.no-profile-title { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.no-profile-sub   { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:360px; }

/* Filter bar */
.filter-bar    { display:flex; align-items:center; flex-wrap:wrap; gap:8px; margin-bottom:18px; }
.filter-search-select { min-width:180px; max-width:220px; }
.filter-select { height:36px; padding:0 28px 0 10px; border:1px solid #dde5f0; border-radius:8px; background:#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23aab8cc'/%3E%3C/svg%3E") no-repeat right 10px center; appearance:none; font-size:13px; color:#1a2b45; cursor:pointer; outline:none; }
.filter-input  { height:36px; padding:0 10px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; background:#fff; }
.filter-count  { font-size:12px; color:#aab8cc; font-weight:600; margin-left:auto; }
.btn-clear     { display:inline-flex; align-items:center; gap:5px; height:36px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; background:#fff; color:#64748b; font-size:12px; cursor:pointer; }
.btn-clear:hover { border-color:#dc2626; color:#dc2626; }
.btn-outline   { display:inline-flex; align-items:center; height:36px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; cursor:pointer; }

/* Period tabs */
.period-tabs   { display:flex; gap:2px; background:#f0f4f8; border-radius:9px; padding:3px; }
.period-tab    { height:30px; padding:0 12px; border:none; background:none; border-radius:7px; font-size:12px; font-weight:500; color:#64748b; cursor:pointer; white-space:nowrap; transition:all .12s; }
.period-tab:hover       { background:#fff; color:#1a2b45; }
.period-tab--active     { background:#fff; color:#004AAD; font-weight:700; box-shadow:0 1px 4px rgba(0,0,0,.1); }

/* Custom date range row */
.date-range     { display:flex; align-items:center; gap:6px; }
.date-range-sep { color:#aab8cc; font-size:13px; }

/* Skeleton */
.sk-card       { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:18px; display:flex; flex-direction:column; gap:12px; }
.sk-head       { height:42px; border-radius:10px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
.sk-body-lines { display:flex; flex-direction:column; gap:8px; }
.sk-line       { border-radius:4px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; height:14px; }
.sk-line--md   { width:60%; }
.sk-line--sm   { width:40%; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Empty */
.empty-box   { display:flex; flex-direction:column; align-items:center; padding:60px 20px; gap:12px; color:#7a90b0; }
.empty-icon  { font-size:44px; opacity:.3; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:340px; }

/* Grid */
.shifts-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; }

/* Card */
.shift-card         { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:18px; display:flex; flex-direction:column; gap:12px; transition:box-shadow .15s; position:relative; }
.shift-card:hover   { box-shadow:0 4px 18px rgba(0,0,0,.07); }
.shift-card--ended  { opacity:.7; }
.shift-card--today  { border-color:#004AAD; box-shadow:0 0 0 2px rgba(0,74,173,.1); }

.today-badge { position:absolute; top:14px; right:14px; background:#004AAD; color:#fff; font-size:10px; font-weight:800; padding:2px 9px; border-radius:20px; letter-spacing:.04em; text-transform:uppercase; }

.sc-head   { display:flex; align-items:center; gap:12px; }
.sc-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.sc-icon--active { background:#e0efff; color:#004AAD; }
.sc-icon--ended  { background:#f1f5f9; color:#94a3b8; }
.sc-info   { flex:1; min-width:0; display:flex; flex-direction:column; gap:4px; }
.sc-name   { font-size:14px; font-weight:700; color:#1a2b45; margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; padding-right:48px; }

.sc-badge      { display:inline-block; font-size:10px; font-weight:700; padding:2px 8px; border-radius:20px; }
.badge--green  { background:#dcfce7; color:#16a34a; }
.badge--gray   { background:#f1f5f9; color:#64748b; }
.badge--purple { background:#f3e8ff; color:#7c3aed; }

.sc-times      { display:flex; align-items:center; gap:10px; background:#f8fafc; border-radius:8px; padding:10px 14px; flex-wrap:wrap; }
.sc-time-block { display:flex; flex-direction:column; gap:2px; }
.sc-label      { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.sc-val        { font-size:14px; font-weight:700; color:#1a2b45; }
.sc-sep        { color:#aab8cc; }
.sc-duration   { margin-left:auto; display:flex; align-items:center; gap:5px; font-size:12px; font-weight:700; color:#004AAD; background:#e0efff; padding:3px 10px; border-radius:20px; white-space:nowrap; }

.sc-client { display:flex; align-items:center; gap:6px; font-size:12px; color:#4a5e78; background:#f0f7ff; border-radius:6px; padding:5px 10px; }

.sc-shift-date { display:flex; align-items:center; gap:6px; font-size:12px; font-weight:600; color:#4a5e78; padding-top:4px; border-top:1px solid #f0f4f8; flex-wrap:wrap; }
.sc-rota-name  { display:flex; align-items:center; gap:6px; font-size:11px; color:#1F6F5C; font-weight:500; margin-top:2px; }
.sc-shift-date--rota { background:#f0faf6; border:1px solid #b3d9cc; border-radius:8px; padding:7px 10px; margin-top:2px; color:#1F6F5C; }
.sc-shift-date--rota svg { color:#1F6F5C; }
.sc-date-text { flex:1; }
.sc-rota-badge { display:inline-block; padding:1px 7px; border-radius:20px; background:#1F6F5C; color:#fff; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; flex-shrink:0; }

/* Clock actions */
.clock-btn         { display:flex; align-items:center; justify-content:center; gap:8px; height:42px; border-radius:10px; font-size:14px; font-weight:700; cursor:pointer; border:none; transition:all .15s; width:100%; margin-top:2px; }
.clock-btn:disabled { opacity:.6; cursor:not-allowed; }
.clock-btn--in     { background:#004AAD; color:#fff; }
.clock-btn--in:hover:not(:disabled)  { background:#003b8e; }
.clock-btn--out    { background:#fee2e2; color:#dc2626; border:1px solid #fca5a5; }
.clock-btn--out:hover:not(:disabled) { background:#fecaca; }

.clock-done     { display:flex; align-items:center; justify-content:center; gap:7px; font-size:13px; font-weight:600; color:#16a34a; background:#dcfce7; border-radius:10px; padding:11px; }
.clock-upcoming { display:flex; align-items:center; justify-content:center; gap:7px; font-size:12px; color:#7a90b0; background:#f8fafc; border-radius:10px; padding:10px; border:1px dashed #dde5f0; }

/* Card clickable */
.shift-card { cursor:pointer; }
.sc-arrow   { color:#aab8cc; font-size:11px; margin-left:auto; flex-shrink:0; transition:transform .15s; }
.shift-card:hover .sc-arrow { transform:translateX(3px); color:#004AAD; }

/* ── Detail modal ──────────────────────────────────────────── */
.modal-box--detail { max-width:500px; max-height:90vh; overflow-y:auto; }

.detail-head      { display:flex; align-items:center; gap:13px; padding:18px 20px; border-bottom:1px solid #f0f4f8; background:#fafbfc; position:sticky; top:0; z-index:10; }
.detail-head-icon { width:44px; height:44px; border-radius:11px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
.detail-head-icon--active { background:#e0efff; color:#004AAD; }
.detail-head-icon--ended  { background:#f1f5f9; color:#94a3b8; }
.detail-head-info { flex:1; min-width:0; }
.detail-shift-name{ font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 5px; }
.detail-badges    { display:flex; flex-wrap:wrap; gap:4px; }
.badge--teal  { background:#e3f0ec; color:#1F6F5C; }
.badge--today { background:#004AAD; color:#fff; }
.detail-close { background:none; border:none; cursor:pointer; color:#94a3b8; font-size:16px; padding:4px; border-radius:6px; margin-left:auto; flex-shrink:0; }
.detail-close:hover { color:#1a2b45; background:#f0f4f8; }

.detail-body { display:flex; flex-direction:column; }

.detail-section { padding:16px 20px; border-bottom:1px solid #f0f4f8; }
.detail-section:last-child { border-bottom:none; }
.detail-section--clock { background:#fafbfc; }
.detail-section-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 10px; display:flex; align-items:center; gap:6px; }

.detail-date      { font-size:14px; font-weight:600; color:#1a2b45; margin-bottom:6px; }
.detail-rota-name { display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:600; color:#1F6F5C; background:#e3f0ec; padding:3px 10px; border-radius:20px; margin-bottom:10px; }

.detail-time-row   { display:flex; align-items:center; gap:14px; background:#f8fafc; border-radius:10px; padding:12px 14px; flex-wrap:wrap; }
.detail-time-block { display:flex; flex-direction:column; gap:2px; }
.detail-time-lbl   { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.detail-time-val   { font-size:15px; font-weight:700; color:#1a2b45; }
.detail-time-arrow { color:#aab8cc; font-size:12px; }
.detail-duration   { margin-left:auto; display:flex; align-items:center; gap:5px; font-size:12px; font-weight:700; color:#004AAD; background:#e0efff; padding:4px 12px; border-radius:20px; white-space:nowrap; }

.detail-client-card { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:13px 15px; display:flex; flex-direction:column; gap:7px; }
.detail-client-row  { display:flex; align-items:flex-start; gap:8px; }
.detail-client-name { font-size:14px; font-weight:700; color:#1a2b45; }
.detail-client-meta { font-size:12px; color:#4a5e78; }
.detail-client-meta svg { color:#7a90b0; font-size:11px; margin-top:1px; flex-shrink:0; }
.detail-map-link    { display:inline-flex; align-items:center; gap:6px; margin-top:4px; font-size:12px; font-weight:600; color:#004AAD; text-decoration:none; background:#e0efff; padding:5px 12px; border-radius:8px; width:fit-content; }
.detail-map-link:hover { background:#c7dffb; }

.coworkers-loading { display:flex; flex-direction:column; gap:6px; }
.coworkers-empty   { display:flex; align-items:center; gap:8px; font-size:13px; color:#aab8cc; background:#f8fafc; border-radius:10px; padding:13px 15px; }
.coworkers-list    { display:flex; flex-direction:column; gap:6px; }
.coworker-row      { display:flex; align-items:center; gap:11px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:10px 13px; }
.coworker-avatar   { width:34px; height:34px; border-radius:50%; background:#004AAD; color:#fff; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.coworker-info     { display:flex; flex-direction:column; gap:2px; min-width:0; }
.coworker-name     { font-size:13px; font-weight:600; color:#1a2b45; }
.coworker-meta     { font-size:11px; color:#7a90b0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

/* Modals */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:3000; display:flex; align-items:center; justify-content:center; padding:20px; }
.modal-box     { background:#fff; border-radius:16px; width:100%; max-width:440px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; }

.modal-header            { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 18px; border-bottom:1px solid #e4eaf2; }
.modal-header-icon       { width:42px; height:42px; border-radius:10px; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-size:18px; }
.modal-header-icon--amber  { background:#fef9c3; color:#ca8a04; }
.modal-header-icon--orange { background:#fff7ed; color:#ea580c; }
.modal-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.modal-sub   { font-size:12px; color:#7a90b0; margin:0; }

.modal-body     { padding:20px 24px 0; }
.modal-label    { display:block; font-size:12px; font-weight:600; color:#4a5e78; margin-bottom:6px; }
.modal-textarea { width:100%; padding:10px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; resize:vertical; outline:none; box-sizing:border-box; font-family:inherit; line-height:1.5; }
.modal-textarea:focus { border-color:#004AAD; }
.modal-char    { font-size:11px; color:#aab8cc; text-align:right; margin:4px 0 0; }
.req           { color:#dc2626; }

.location-dist-card { margin:14px 24px 0; background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:12px 14px; display:flex; flex-direction:column; gap:5px; }
.dist-name  { font-size:13px; font-weight:700; color:#1a2b45; }
.dist-badge         { display:flex; align-items:center; gap:6px; font-size:12px; color:#92400e; }
.dist-badge--red    { color:#dc2626; }
.modal-header-icon--red { background:#fee2e2; color:#dc2626; }

.modal-footer { display:flex; justify-content:flex-end; gap:8px; padding:16px 24px; border-top:1px solid #f0f4f8; margin-top:16px; }
.btn-cancel  { display:inline-flex; align-items:center; height:38px; padding:0 16px; border-radius:8px; border:1px solid #dde5f0; background:#fff; font-size:13px; font-weight:600; color:#1a2b45; cursor:pointer; }
.btn-cancel:hover { background:#f5f7fa; }
.btn-confirm { display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 18px; border-radius:8px; border:none; font-size:13px; font-weight:600; color:#fff; cursor:pointer; transition:background .15s; }
.btn-confirm:disabled { opacity:.55; cursor:not-allowed; }
.btn-confirm--amber  { background:#ca8a04; }
.btn-confirm--amber:hover:not(:disabled) { background:#a16207; }
.btn-confirm--orange { background:#ea580c; }
.btn-confirm--orange:hover:not(:disabled) { background:#c2410c; }

/* View toggle */
.view-toggle     { display:flex; border:1px solid #dde5f0; border-radius:8px; overflow:hidden; }
.view-btn        { width:34px; height:34px; border:none; background:#fff; color:#aab8cc; font-size:13px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .12s; }
.view-btn:hover  { background:#f0f4f8; color:#1a2b45; }
.view-btn--active { background:#004AAD; color:#fff; }

/* Table view */
.tbl-wrap  { overflow-x:auto; border:1px solid #e4eaf2; border-radius:12px; background:#fff; }
.shifts-tbl { width:100%; border-collapse:collapse; font-size:13px; }
.shifts-tbl thead th { padding:10px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.tbl-row         { border-bottom:1px solid #f0f4f8; cursor:pointer; transition:background .1s; }
.tbl-row:last-child { border-bottom:none; }
.tbl-row:hover   { background:#f8fafc; }
.tbl-row--today  { background:#eff6ff; }
.tbl-row--today:hover { background:#dbeafe; }
.tbl-row--ended  { opacity:.7; }
.shifts-tbl td   { padding:10px 14px; vertical-align:middle; color:#1a2b45; }
.tbl-shift-name  { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 3px; white-space:nowrap; }
.tbl-badges      { display:flex; gap:3px; }
.badge--today-sm { background:#004AAD; color:#fff; }
.tbl-date        { font-size:12px; color:#4a5e78; max-width:180px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tbl-mono        { font-size:12px; font-family:monospace; white-space:nowrap; color:#1a2b45; }
.tbl-dur         { color:#004AAD; font-weight:700; }
.tbl-client      { font-size:12px; color:#4a5e78; white-space:nowrap; }
.tbl-rota        { white-space:nowrap; }
.tbl-rota-name   { display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:600; color:#1F6F5C; background:#e3f0ec; padding:2px 8px; border-radius:20px; }
.tbl-muted       { color:#aab8cc; font-size:12px; }
.tbl-action      { white-space:nowrap; }
.tbl-clocked     { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-weight:600; color:#16a34a; }
.tbl-clock-btn   { display:inline-flex; align-items:center; gap:5px; height:30px; padding:0 10px; border-radius:7px; font-size:12px; font-weight:600; cursor:pointer; border:none; white-space:nowrap; }
.tbl-clock-btn--in  { background:#004AAD; color:#fff; }
.tbl-clock-btn--out { background:#fee2e2; color:#dc2626; border:1px solid #fca5a5; }
.tbl-clock-btn:disabled { opacity:.6; cursor:not-allowed; }
.tbl-detail-btn  { background:none; border:none; cursor:pointer; color:#aab8cc; font-size:12px; padding:4px 8px; border-radius:6px; }
.tbl-detail-btn:hover { background:#f0f4f8; color:#004AAD; }

/* Pagination */
.pagination { display:flex; align-items:center; gap:4px; margin-top:16px; justify-content:center; flex-wrap:wrap; }
.pg-btn     { min-width:34px; height:34px; padding:0 8px; border:1px solid #e4eaf2; border-radius:8px; background:#fff; color:#1a2b45; font-size:13px; font-weight:500; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .12s; }
.pg-btn:hover:not(:disabled) { background:#eff6ff; border-color:#bfdbfe; color:#004AAD; }
.pg-btn--active { background:#004AAD; color:#fff; border-color:#004AAD; font-weight:700; }
.pg-btn--active:hover { background:#003b8e; border-color:#003b8e; color:#fff; }
.pg-btn--ellipsis { cursor:default; color:#aab8cc; border-color:#f0f4f8; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-info    { margin-left:8px; font-size:12px; color:#7a90b0; white-space:nowrap; }

/* ── Shift assignment notifications ──────────────────────────── */
.shift-notif-list { display:flex; flex-direction:column; gap:8px; margin-bottom:16px; }
.shift-notif      { display:flex; align-items:flex-start; gap:12px; background:#f0fdf4; border:1px solid #bbf7d0; border-radius:10px; padding:12px 14px; }
.sn-icon          { width:34px; height:34px; border-radius:8px; background:#dcfce7; color:#16a34a; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
.sn-body          { flex:1; min-width:0; }
.sn-title         { font-size:12px; font-weight:700; color:#15803d; margin:0 0 2px; text-transform:uppercase; letter-spacing:.04em; }
.sn-msg           { font-size:13px; color:#166534; margin:0; line-height:1.5; }
.sn-dismiss       { background:none; border:none; cursor:pointer; color:#86efac; font-size:13px; padding:4px; border-radius:6px; flex-shrink:0; transition:all .12s; }
.sn-dismiss:hover { background:#dcfce7; color:#16a34a; }
</style>
